import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Building2,
  CheckCircle2,
  Clock,
  Inbox,
  UserPlus,
  Users,
  XCircle,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import React from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TenantOnboardingWizard,
  isTenantOnboardingDone,
} from '@/components/enterprise/TenantOnboardingWizard';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import {
  approveWalkIn,
  denyWalkIn,
  getEnterpriseApprovalQueue,
  getEnterpriseExpectedToday,
  getEnterpriseOnPremises,
  getEnterpriseWorkerPasses,
} from '@/services/api.client';
import { formatDateTime, formatDate, statusBadgeClass } from '@/lib/utils';

// ─── Quick Action Card ────────────────────────────────────────────────────────

function QuickAction({
  icon: Icon,
  label,
  to,
  variant = 'default',
}: {
  icon: React.ElementType;
  label: string;
  to: string;
  variant?: 'default' | 'secondary';
}) {
  return (
    <Button
      asChild
      variant={variant === 'secondary' ? 'outline' : 'default'}
      className="h-auto flex-col gap-2 py-4 px-5"
    >
      <Link to={to}>
        <Icon className="size-5" />
        <span className="text-sm font-medium">{label}</span>
      </Link>
    </Button>
  );
}

// ─── Stat Pill (compact, secondary to the live feeds) ─────────────────────────

function StatPill({
  label,
  value,
  loading,
  alert,
}: {
  label: string;
  value: number;
  loading?: boolean;
  alert?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border px-3 py-2 ${
        alert ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card'
      }`}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      {loading ? (
        <Skeleton className="h-5 w-8 mt-1" />
      ) : (
        <p className={`text-lg font-semibold ${alert ? 'text-destructive' : ''}`}>{value}</p>
      )}
    </div>
  );
}

// ─── Enterprise Home — Reception View ─────────────────────────────────────────

export function EnterprisePage() {
  const ctx = useEnterpriseContext();
  const { user } = useAuth();
  const qc = useQueryClient();

  // First-login onboarding wizard — opens automatically the first time the
  // tenant admin lands here, then stays dismissed thereafter (localStorage
  // flag keyed by user + tenant). ADR 009 slice 3 (tier 1).
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  useEffect(() => {
    if (!user || !ctx) return;
    if (!isTenantOnboardingDone(user.id, ctx.tenantId)) {
      setOnboardingOpen(true);
    }
  }, [user, ctx]);

  const expectedQuery = useQuery({
    queryKey: ['enterprise-expected-today', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseExpectedToday(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
    refetchInterval: 30_000,
  });

  const onPremisesQuery = useQuery({
    queryKey: ['enterprise-on-premises', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseOnPremises(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
    refetchInterval: 30_000,
  });

  const queueQuery = useQuery({
    queryKey: ['enterprise-approval-queue', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseApprovalQueue(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
    refetchInterval: 15_000,
  });

  const { data: workerPasses } = useQuery({
    queryKey: ['enterprise-worker-passes', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseWorkerPasses(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
  });

  const invalidateLiveFeeds = () => {
    void qc.invalidateQueries({ queryKey: ['enterprise-approval-queue'] });
    void qc.invalidateQueries({ queryKey: ['enterprise-on-premises'] });
    void qc.invalidateQueries({ queryKey: ['enterprise-expected-today'] });
  };

  const approveMut = useMutation({
    mutationFn: (id: string) => approveWalkIn(id),
    onSuccess: () => {
      invalidateLiveFeeds();
      toast.success('Visitor approved');
    },
    onError: () => toast.error('Failed to approve visitor'),
  });

  const denyMut = useMutation({
    mutationFn: (id: string) => denyWalkIn(id),
    onSuccess: () => {
      invalidateLiveFeeds();
      toast.success('Visitor denied');
    },
    onError: () => toast.error('Failed to deny visitor'),
  });

  const expectedCount = expectedQuery.data?.length ?? 0;
  const onPremisesCount = onPremisesQuery.data?.length ?? 0;
  const queueCount = queueQuery.data?.length ?? 0;
  const activePassCount = workerPasses?.filter((p) => p.status === 'active').length ?? 0;

  return (
    <div className="space-y-6 max-w-4xl">
      {ctx && user && (
        <TenantOnboardingWizard
          userId={user.id}
          propertyId={ctx.propertyId}
          tenantId={ctx.tenantId}
          open={onboardingOpen}
          onClose={() => setOnboardingOpen(false)}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl">Company Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {new Date().toLocaleDateString('en-KE', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Compact stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill label="Expected today" value={expectedCount} loading={expectedQuery.isLoading} />
        <StatPill
          label="On premises now"
          value={onPremisesCount}
          loading={onPremisesQuery.isLoading}
        />
        <StatPill
          label="Approval queue"
          value={queueCount}
          loading={queueQuery.isLoading}
          alert={queueCount > 0}
        />
        <StatPill label="Active worker passes" value={activePassCount} />
      </div>

      {/* Approval queue — only shown when non-empty */}
      {queueCount > 0 && (
        <Card className="border-destructive/30">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Inbox className="size-4 text-destructive" />
                Approval queue ({queueCount})
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border/50">
              {queueQuery.data?.map((item) => (
                <div key={item.id} className="flex items-center gap-3 py-3">
                  <div className="size-8 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                    <Users className="size-4 text-destructive" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.visitorName}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.hostType === 'enterprise_employee'
                        ? `For ${item.hostEmployeeName ?? 'employee'}`
                        : 'For Reception'}
                      {' • '}
                      {formatDateTime(item.requestedAt)}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => approveMut.mutate(item.id)}
                      disabled={
                        (approveMut.isPending && approveMut.variables === item.id) ||
                        (denyMut.isPending && denyMut.variables === item.id)
                      }
                    >
                      <CheckCircle2 className="size-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => denyMut.mutate(item.id)}
                      disabled={
                        (approveMut.isPending && approveMut.variables === item.id) ||
                        (denyMut.isPending && denyMut.variables === item.id)
                      }
                    >
                      <XCircle className="size-4 mr-1" />
                      Deny
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two columns: Expected today + On premises now */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="size-4" />
              Expected today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <QueryStateBoundary
              query={expectedQuery}
              isEmpty={(d) => (d ?? []).length === 0}
              errorTitle="Couldn't load expected visitors"
              loadingFallback={
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="size-8 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-40" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </div>
                  ))}
                </div>
              }
              emptyFallback={
                <div className="py-6 text-center">
                  <Clock className="size-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No visitors expected today.</p>
                </div>
              }
            >
              {(items) => (
                <div className="divide-y divide-border/50">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 py-3">
                      <div className="size-8 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                        {item.kind === 'walk_in' ? (
                          <Inbox className="size-4 text-primary" />
                        ) : (
                          <Users className="size-4 text-primary" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.visitorName}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {item.hostName ? `For ${item.hostName}` : 'No host'} •{' '}
                          {formatDateTime(item.expectedAt)}
                        </p>
                      </div>
                      <Badge className={statusBadgeClass(item.status)}>{item.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </QueryStateBoundary>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="size-4" />
              On premises now
            </CardTitle>
          </CardHeader>
          <CardContent>
            <QueryStateBoundary
              query={onPremisesQuery}
              isEmpty={(d) => (d ?? []).length === 0}
              errorTitle="Couldn't load on-premises list"
              loadingFallback={
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Skeleton className="size-8 rounded-full" />
                      <div className="flex-1 space-y-1.5">
                        <Skeleton className="h-3.5 w-40" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </div>
                  ))}
                </div>
              }
              emptyFallback={
                <div className="py-6 text-center">
                  <Building2 className="size-8 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No visitors on premises.</p>
                </div>
              }
            >
              {(items) => (
                <div className="divide-y divide-border/50">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-center gap-3 py-3">
                      <span className="size-2 rounded-full bg-[var(--status-green)] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.visitorName}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {item.hostName ? `With ${item.hostName}` : 'Visiting'} •{' '}
                          {formatDate(item.checkedInAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </QueryStateBoundary>
          </CardContent>
        </Card>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <QuickAction icon={UserPlus} label="Invite Visitor" to="/enterprise/visitors" />
        <QuickAction
          icon={Briefcase}
          label="Issue Worker Pass"
          to="/enterprise/worker-passes"
          variant="secondary"
        />
      </div>
    </div>
  );
}
